import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Insert, type Row } from '@/lib/supabase'
import { pipelineKeys } from '@/features/pipeline/queries'

export type Inquiry = Row<'inquiries'>
export type InquiryStatus = Inquiry['status']

/** Statuses that still want something from her, in the order she works them. */
export const OPEN_STATUSES: InquiryStatus[] = ['new', 'reviewing', 'quoted']

export const inquiryKeys = {
  list: (filter: string) => ['inquiries', filter] as const,
  detail: (id: string) => ['inquiry', id] as const,
}

const INQUIRY_SELECT = `
  *,
  venue:venues!inquiries_venue_id_fkey ( id, name, town, distance_km ),
  job:jobs!inquiries_converted_job_fk ( id, job_number, contract_signed_at, deposit_status, booked_at )
` as const

export interface InquiryWithRelations extends Inquiry {
  venue: { id: string; name: string; town: string | null; distance_km: number | null } | null
  job: {
    id: string
    job_number: number
    contract_signed_at: string | null
    deposit_status: Row<'jobs'>['deposit_status']
    booked_at: string | null
  } | null
}

export function useInquiries(filter: 'open' | 'all' | 'archived' = 'open') {
  return useQuery({
    queryKey: inquiryKeys.list(filter),
    queryFn: async (): Promise<InquiryWithRelations[]> => {
      let query = supabase.from('inquiries').select(INQUIRY_SELECT).order('received_at', { ascending: false })

      if (filter === 'archived') {
        query = query.not('archived_at', 'is', null)
      } else {
        query = query.is('archived_at', null)
        if (filter === 'open') query = query.in('status', OPEN_STATUSES)
      }

      const { data, error } = await query
      if (error) throw error
      return data as unknown as InquiryWithRelations[]
    },
  })
}

export function useInquiry(id: string | undefined) {
  return useQuery({
    queryKey: inquiryKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async (): Promise<InquiryWithRelations> => {
      const { data, error } = await supabase.from('inquiries').select(INQUIRY_SELECT).eq('id', id ?? '').single()
      if (error) throw error
      return data as unknown as InquiryWithRelations
    },
  })
}

export function useCreateInquiry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values: Insert<'inquiries'>) => {
      const { data, error } = await supabase.from('inquiries').insert(values).select('id').single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] })
    },
    onError: (error) => {
      toast.error('Could not save the enquiry', { description: describeError(error) })
    },
  })
}

export function useUpdateInquiry(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (patch: Partial<Inquiry>) => {
      const { error } = await supabase.from('inquiries').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inquiryKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] })
    },
    onError: (error) => {
      toast.error('Could not save', { description: describeError(error) })
    },
  })
}

/**
 * Enquiry to wedding.
 *
 * Calls the database function rather than doing three writes from here: creating
 * two contacts, creating the job and updating the enquiry has to be atomic, and
 * PostgREST cannot wrap separate requests in a transaction. See the
 * `convert_inquiry_to_job` migration.
 */
export function useConvertInquiry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (inquiryId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('convert_inquiry_to_job', { p_inquiry_id: inquiryId })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.board })
    },
    onError: (error) => {
      toast.error('Could not create the wedding', { description: describeError(error) })
    },
  })
}

/**
 * The two ticks that book a wedding, from the enquiry: contract signed and
 * deposit received. The quote lives on a wedding, so an enquiry that is not one
 * yet becomes one first; the wedding then moves to Booked by itself once both
 * are ticked (jobs_auto_stage), and the enquiry is marked booked with it.
 */
export function useBookingTick(inquiry: InquiryWithRelations) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (patch: { contract_signed_at?: string | null; deposit_status?: Row<'jobs'>['deposit_status'] }) => {
      let jobId = inquiry.job?.id ?? null
      if (!jobId) {
        const { data, error } = await supabase.rpc('convert_inquiry_to_job', { p_inquiry_id: inquiry.id })
        if (error) throw error
        jobId = data
      }
      const { data: job, error } = await supabase.from('jobs').update(patch).eq('id', jobId).select('booked_at').single()
      if (error) throw error
      const status = job.booked_at ? 'booked' : inquiry.status === 'booked' ? 'quoted' : inquiry.status
      if (status !== inquiry.status) {
        const { error: statusError } = await supabase.from('inquiries').update({ status }).eq('id', inquiry.id)
        if (statusError) throw statusError
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] })
      void queryClient.invalidateQueries({ queryKey: inquiryKeys.detail(inquiry.id) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.board })
    },
    onError: (error) => {
      toast.error('Could not save that', { description: describeError(error) })
    },
  })
}

/** How long an enquiry has waited, and whether that is now a problem. */
export function inquiryUrgency(inquiry: Inquiry): 'fresh' | 'waiting' | 'late' {
  if (inquiry.status !== 'new') return 'fresh'

  const hours = (Date.now() - new Date(inquiry.received_at).getTime()) / 3_600_000
  if (hours < 24) return 'fresh'
  if (hours < 72) return 'waiting'
  return 'late'
}
